import React, { useEffect, useState } from "react";
import DashboardLayout from "../../layouts/Dashboard";
import { dashboardCRUD } from "../../http";
import { yearEnd, yearStart } from "../../functions/dateHelpers";
import FilterDatePicker from "../../components/DatePicker/FilterDatePiecker";
import { Stack, Grid, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {sum} from "../../functions/linqSimulation";
import { roundTo2 } from "../../functions/formatters";
import XLSX from "xlsx-js-style";
import { formatDate, MOMENT_FORMAT_DATE_DMYHM_DOTS } from "../../functions/formatters";
import { ContractSelect, FilialSelect, FilialDivisionSelect, ObjectSectionSelect, ObjectSubSectionSelect } from "../../components/Select/domainSelects";
import DomainSelect from "../../components/Select/DomainSelect";
import { PurposeOfSelectionsSelectFiltered } from "../../components/Select/domainSelects";
import AnObjectField from "../../components/Relations/AnObjectField";
import {
    normalizePurposeName,
    OBJECT_SECTION_EXCLUDE,
    OUTBREAK_SECTION_NAME,
    EPIDEMIOLOGICAL_DEPARTMENT_SECTION_NAME,
    OUTBREAK_SUBSECTION_ALLOWED,
    OUTBREAK_SUBSECTION_WITH_SUBSUBSECTION,
    OUTBREAK_SUBSUBSECTION_OPTIONS,
    filterObjectSection,
    filterObjectSubSection,
    shouldShowSubSubSection
} from "../../functions/objectSectionFilters";

// Функция для безопасного форматирования чисел
const safeRoundTo2 = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 0;
    }
    return roundTo2(value);
};

const getMetricValue = (item, key) => {
    const upperKey = key.charAt(0).toUpperCase() + key.slice(1);
    return safeRoundTo2(item?.[key] ?? item?.[upperKey] ?? 0);
};

const VISIBLE_DEPARTMENTS = [
    { index: 1, name: "Бактериологическая лаборатория" },
    { index: 2, name: "Вирусологическая лаборатория" },
    { index: 4, name: "Лаборатория карантинных и особо опасных инфекций" },
    { index: 6, name: "Радиологическая лаборатория" },
    { index: 7, name: "Санитарно-химическая лаборатория" },
];

const VISIBLE_DEPARTMENT_INDICES = VISIBLE_DEPARTMENTS.map((d) => d.index);
const VISIBLE_GROUP_COUNT = VISIBLE_DEPARTMENTS.length + 1; // лаборатории + Итого

const getPlanQuantity = (item, index) =>
    getMetricValue(item, `planQuantity${index}`);

const getPlanSum = (item, index) =>
    getMetricValue(item, `planSum${index}`);

const getFactQuantity = (item, index) =>
    safeRoundTo2(item?.[`quantity${index}`] ?? item?.[`Quantity${index}`] ?? 0);

const getInconsistenceQuantity = (item, index) =>
    safeRoundTo2(
        item?.[`inconsistenceQuantity${index}`] ??
        item?.[`InconsistenceQuantity${index}`] ??
        0
    );

const getPlanTotalQuantity = (item) =>
    safeRoundTo2(
        VISIBLE_DEPARTMENT_INDICES.reduce(
            (acc, index) => acc + getPlanQuantity(item, index),
            0
        )
    );

const getPlanTotalSum = (item) =>
    safeRoundTo2(
        VISIBLE_DEPARTMENT_INDICES.reduce(
            (acc, index) => acc + getPlanSum(item, index),
            0
        )
    );

const getTotalSumValue = (item) =>
    safeRoundTo2(
        VISIBLE_DEPARTMENT_INDICES.reduce(
            (acc, index) => acc + getMetricValue(item, `sum${index}`),
            0
        )
    );

const getTotalQuantity = (item) =>
    safeRoundTo2(
        VISIBLE_DEPARTMENT_INDICES.reduce(
            (acc, index) => acc + getFactQuantity(item, index),
            0
        )
    );

const getTotalInconsistence = (item) =>
    safeRoundTo2(
        VISIBLE_DEPARTMENT_INDICES.reduce(
            (acc, index) => acc + getInconsistenceQuantity(item, index),
            0
        )
    );

const calcPercent = (fact, plan) => {
    if (!plan || plan === 0) return 0;
    return safeRoundTo2((fact / plan) * 100);
};

const RequestTarificationsQuantityDepartments = () => {
    const start = yearStart();
    const end = yearEnd();
    
    const [query, setQuery] = useState({
        paging: { skip: 0 },
        filter: {
            researchEndDateTime: {
                operand1: start,
                operand2: end,
                operator: "between"
            }
        }
    });

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [filters, setFilters] = useState({
        anObjectSectionId: null,
        anObjectSubSectionId: null,
        anObjectSubSubSectionName: null,
        purposeOfSelectionId: null,
        governmentContractId: null,
        filialId: null,
        filialDivisionId: null,
        anObjectId: null,
        referral: null
    });
    const [selectedAnObject, setSelectedAnObject] = useState(null);
    const [selectedObjectSectionName, setSelectedObjectSectionName] = useState("");
    const [selectedObjectSubSectionName, setSelectedObjectSubSectionName] = useState("");

    const showSubSubSection = shouldShowSubSubSection(selectedObjectSubSectionName);

    const resolveObjectName = (item) => {
        const fromItem = (
            item?.anObjectName ||
            item?.targetObjectName ||
            item?.objectName ||
            item?.nameOfObject ||
            item?.anObjectTitle ||
            item?.targetObjectTitle ||
            item?.targetObject?.name ||
            item?.anObject?.name ||
            item?.referral?.targetObject?.name ||
            item?.referral?.anObject?.name ||
            ""
        );

        if (fromItem) {
            return fromItem;
        }

        // Если бекенд не возвращает имя объекта в агрегате, показываем выбранный в фильтре объект.
        if (filters.anObjectId) {
            return (
                selectedAnObject?.name ||
                selectedAnObject?.fullName ||
                selectedAnObject?.shortName ||
                selectedAnObject?.title ||
                ""
            );
        }

        return "";
    };

    const buildRequestQuery = () => {
        const requestQuery = {
            ...query,
            filter: { ...query.filter }
        };

        const referralFilter = { ...(requestQuery.filter.referral || {}) };

        if (filters.filialId) {
            requestQuery.filter.filialId = {
                operand1: filters.filialId,
                operator: "equals"
            };
        } else {
            delete requestQuery.filter.filialId;
        }

        if (filters.filialDivisionId) {
            requestQuery.filter.filialDivisionId = {
                operand1: filters.filialDivisionId,
                operator: "equals"
            };
        } else {
            delete requestQuery.filter.filialDivisionId;
        }

        const targetObjectFilter = {};

        if (filters.anObjectSectionId) {
            targetObjectFilter.sectionId = {
                operand1: filters.anObjectSectionId,
                operator: "equals"
            };
        }

        if (filters.anObjectSubSectionId) {
            targetObjectFilter.subSectionId = {
                operand1: filters.anObjectSubSectionId,
                operator: "equals"
            };
        }

        if (Object.keys(targetObjectFilter).length > 0) {
            referralFilter.targetObject = targetObjectFilter;
        }

        if (filters.purposeOfSelectionId) {
            referralFilter.purposeOfSelectionId = {
                operand1: filters.purposeOfSelectionId,
                operator: "equals"
            };
        }

        if (filters.anObjectId) {
            referralFilter.targetObjectId = {
                operand1: filters.anObjectId,
                operator: "equals"
            };
            referralFilter.targetObject = {
                ...(referralFilter.targetObject || {}),
                id: {
                    operand1: filters.anObjectId,
                    operator: "equals"
                }
            };
        }

        if (filters.governmentContractId) {
            referralFilter.governmentContractId = {
                operand1: filters.governmentContractId,
                operator: "equals"
            };
        }

        if (Object.keys(referralFilter).length > 0) {
            requestQuery.filter.referral = referralFilter;
        } else {
            delete requestQuery.filter.referral;
        }

        return requestQuery;
    };

    const fetchAsync = async () => {
        setLoading(true);        

        const response = await dashboardCRUD.post('requestTarifications/quantity/departments', buildRequestQuery());
        setData(response);

        setLoading(false);
    }

    const handleExcelExport = async () => {
        if (data.length === 0) {
            alert('Нет данных для экспорта. Сначала сформируйте отчет.');
            return;
        }

        setExportLoading(true);

        try {
            const thinBorder = {style: 'thin', color: 'auto'};
            const thinBorders = {top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder};
            const thickBorder = {style: 'medium', color: 'auto'};
            const thickBorders = {top: thickBorder, bottom: thickBorder, left: thickBorder, right: thickBorder};
            const center10 = {alignment: {horizontal: 'center'}, font: {sz: 10}};
            const centerBold10 = {alignment: {horizontal: 'center'}, font: {sz: 10, bold: true}};
            const centerBold10_2 = {
                alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
                border: thinBorders,
                font: {sz: 9, bold: true}
            };

            const wb = XLSX.utils.book_new();

            const cell = (text, style = center10) => {
                return {
                    t: "s",
                    v: text,
                    s: style
                };
            }

            const numberCell = (value, style = center10) => {
                return {
                    t: "n",
                    v: value,
                    s: style
                };
            }

            const labHeaderCells = () => [
                cell("", centerBold10_2),
                cell("", centerBold10_2),
                cell("", centerBold10_2),
                cell("", centerBold10_2),
                cell("", centerBold10_2),
            ];

            const labSubHeaderCells = () => [
                cell("план", centerBold10),
                cell("сумма плана", centerBold10),
                cell("факт", centerBold10),
                cell("%", centerBold10),
                cell("сумма", centerBold10),
                cell("не соответствие", centerBold10),
            ];

            // Создаем заголовки таблицы
            const aoa = [
                [
                    cell("№", centerBold10),
                    cell("Договора c ДСЭК (Регионы)", centerBold10),
                    cell("Наименование объекта", centerBold10),
                    ...VISIBLE_DEPARTMENTS.flatMap((dept) => [
                        cell(dept.name, centerBold10_2),
                        ...labHeaderCells(),
                    ]),
                    cell("Итого", centerBold10_2),
                    ...labHeaderCells(),
                ],
                [
                    cell("", centerBold10),
                    cell("", centerBold10),
                    cell("", centerBold10),
                    ...Array.from({ length: VISIBLE_GROUP_COUNT }).flatMap(() => labSubHeaderCells()),
                ]
            ];

            // Добавляем данные
            data.forEach((item, index) => {
                const totalQuantity = getTotalQuantity(item);
                const totalInconsistence = getTotalInconsistence(item);
                const row = [
                    cell((index + 1).toString()),
                    cell(item.name || ""),
                    cell(resolveObjectName(item)),
                ];
                VISIBLE_DEPARTMENT_INDICES.forEach((i) => {
                    const plan = getPlanQuantity(item, i);
                    const planSum = getPlanSum(item, i);
                    const fact = getFactQuantity(item, i);
                    const percent = calcPercent(fact, plan);
                    const sumValue = getMetricValue(item, `sum${i}`);
                    const inconsistence = getInconsistenceQuantity(item, i);
                    row.push(
                        numberCell(plan),
                        numberCell(planSum),
                        numberCell(fact),
                        numberCell(percent),
                        numberCell(sumValue),
                        numberCell(inconsistence)
                    );
                });
                const totalPlan = getPlanTotalQuantity(item);
                const totalPlanSum = getPlanTotalSum(item);
                const totalPercent = calcPercent(totalQuantity, totalPlan);
                row.push(
                    numberCell(totalPlan),
                    numberCell(totalPlanSum),
                    numberCell(totalQuantity),
                    numberCell(totalPercent),
                    numberCell(getTotalSumValue(item)),
                    numberCell(totalInconsistence)
                );
                aoa.push(row);
            });

            // Добавляем итоговую строку
            if (data.length > 0) {
                const totalRow = [
                    cell(""),
                    cell("Всего:", centerBold10),
                    cell(""),
                ];
                VISIBLE_DEPARTMENT_INDICES.forEach((i) => {
                    const plan = safeRoundTo2(sum(data, _ => getPlanQuantity(_, i)));
                    const planSum = safeRoundTo2(sum(data, _ => getPlanSum(_, i)));
                    const fact = safeRoundTo2(sum(data, _ => getFactQuantity(_, i)));
                    const percent = calcPercent(fact, plan);
                    const sumValue = safeRoundTo2(sum(data, _ => getMetricValue(_, `sum${i}`)));
                    const inconsistence = safeRoundTo2(sum(data, _ => getInconsistenceQuantity(_, i)));
                    totalRow.push(
                        numberCell(plan),
                        numberCell(planSum),
                        numberCell(fact),
                        numberCell(percent),
                        numberCell(sumValue),
                        numberCell(inconsistence)
                    );
                });
                const totalPlan = safeRoundTo2(sum(data, _ => getPlanTotalQuantity(_)));
                const totalPlanSum = safeRoundTo2(sum(data, _ => getPlanTotalSum(_)));
                const totalPercent = calcPercent(totalQuantityAll, totalPlan);
                totalRow.push(
                    numberCell(totalPlan),
                    numberCell(totalPlanSum),
                    numberCell(totalQuantityAll),
                    numberCell(totalPercent),
                    numberCell(totalSumAll),
                    numberCell(totalInconsistenceAll)
                );
                aoa.push(totalRow);
            }

            const sheet = XLSX.utils.aoa_to_sheet(aoa);
            
            // Устанавливаем ширину колонок
            sheet['!cols'] = [
                {width: 8}, {width: 30}, {width: 30},
                ...Array(VISIBLE_GROUP_COUNT).fill(null).flatMap(() => ([
                    {width: 12}, // план
                    {width: 14}, // сумма плана
                    {width: 12}, // факт
                    {width: 8},  // %
                    {width: 12}, // сумма
                    {width: 15}, // не соответствие
                ]))
            ];

            // Устанавливаем высоту строк
            sheet['!rows'] = [
                {hpx: 60, hpt: 10}, {hpx: 30, hpt: 5}, 
                ...Array(data.length + 1).fill({hpx: 25, hpt: 4})
            ];

            XLSX.utils.book_append_sheet(wb, sheet, 'Отчет');
            
            const output = await XLSX.write(wb, {type: 'array'});
            let blob = await new Blob([output], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
            let objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a')
            link.href = objectUrl
            link.download = `Планирование_и_исполнение_исследований_госзаказа_по_лабораториям_${formatDate(query.filter.researchEndDateTime.operand1, 'DD.MM.YYYY')}_${formatDate(query.filter.researchEndDateTime.operand2, 'DD.MM.YYYY')}.xlsx`
            document.body.appendChild(link)
            link.click()
            link.remove()
        } catch (error) {
            console.error('Ошибка при экспорте:', error);
            alert('Ошибка при экспорте отчета в Excel');
        } finally {
            setExportLoading(false);
        }
    }

    const totalQuantityAll = safeRoundTo2(sum(data, _ => getTotalQuantity(_)));
    const totalInconsistenceAll = safeRoundTo2(sum(data, _ => getTotalInconsistence(_)));
    const totalSumAll = safeRoundTo2(sum(data, _ => getTotalSumValue(_)));

    return (
        <DashboardLayout>
            <Stack direction="row" alignItems="center" justifyContent="normal">
                <label style={{minWidth:"360px"}}>Планирование и исполнение исследований госзаказа по лабораториям за ПЕРИОД</label>
                <FilterDatePicker key="start" label="от" value={query.filter.researchEndDateTime.operand1} handleFunction={(value) =>
                {
                    if (!value) {
                        return;
                    }
                    query.filter.researchEndDateTime.operand1 = value;
                    setQuery({...query});
                }} style={{width:"250px"}} />
                <FilterDatePicker key="start" label="до" value={query.filter.researchEndDateTime.operand2} handleFunction={(value) =>
                {
                    if (!value) {
                        return;
                    }
                    query.filter.researchEndDateTime.operand2 = value;
                    setQuery({...query});
                }} style={{width:"250px", marginLeft: "10px"}} />
            </Stack>

            <Grid container spacing={2} sx={{ marginTop: 2, marginBottom: 2 }}>
                <Grid item xs={12} md={3}>
                    <ContractSelect
                        label="Договор ДСЭК"
                        year={(new Date()).getFullYear()}
                        fullWidth
                        margin="dense"
                        value={filters.governmentContractId}
                        onChange={(val) => setFilters(prev => ({
                            ...prev,
                            governmentContractId: val || null
                        }))}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <FilialSelect
                        label="Филиал"
                        fullWidth
                        margin="dense"
                        value={filters.filialId}
                        onChange={(val) => setFilters(prev => ({
                            ...prev,
                            filialId: val || null,
                            filialDivisionId: null
                        }))}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <FilialDivisionSelect
                        label="Районное отделение"
                        filialId={filters.filialId}
                        fullWidth
                        margin="dense"
                        value={filters.filialDivisionId}
                        onChange={(val) => setFilters(prev => ({
                            ...prev,
                            filialDivisionId: val || null
                        }))}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <ObjectSectionSelect
                        label="Раздел объекта"
                        fullWidth
                        margin="dense"
                        value={filters.anObjectSectionId}
                        filter={filterObjectSection}
                        onChange={(val, opt) => {
                            setSelectedObjectSectionName(opt?.name || "");
                            setSelectedObjectSubSectionName("");
                            setFilters(prev => ({
                                ...prev,
                                anObjectSectionId: val || null,
                                anObjectSubSectionId: null,
                                anObjectSubSubSectionName: null
                            }));
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <ObjectSubSectionSelect
                        label="Подраздел объекта"
                        sectionId={filters.anObjectSectionId}
                        fullWidth
                        margin="dense"
                        value={filters.anObjectSubSectionId}
                        filter={(opt) => filterObjectSubSection(opt, selectedObjectSectionName)}
                        onChange={(val, opt) => {
                            setSelectedObjectSubSectionName(opt?.name || "");
                            const subSectionId = val || null;
                            const selectedName = normalizePurposeName(opt?.name);
                            const allowSubSubSection = OUTBREAK_SUBSECTION_WITH_SUBSUBSECTION.has(selectedName);
                            setFilters(prev => ({
                                ...prev,
                                anObjectSubSectionId: subSectionId,
                                anObjectSubSubSectionName: allowSubSubSection ? prev.anObjectSubSubSectionName : null
                            }));
                        }}
                    />
                </Grid>
                {showSubSubSection && (
                    <Grid item xs={12} md={3}>
                        <DomainSelect
                            label="Подподраздел объекта"
                            initialOptions={OUTBREAK_SUBSUBSECTION_OPTIONS}
                            optionValueKey="name"
                            optionLabelKey="name"
                            fullWidth
                            margin="dense"
                            value={filters.anObjectSubSubSectionName}
                            onChange={(val) => setFilters(prev => ({
                                ...prev,
                                anObjectSubSubSectionName: val || null
                            }))}
                        />
                    </Grid>
                )}
                <Grid item xs={12} md={3}>
                    <PurposeOfSelectionsSelectFiltered
                        fullWidth
                        margin="dense"
                        value={filters.purposeOfSelectionId}
                        onChange={(val) => setFilters(prev => ({
                            ...prev,
                            purposeOfSelectionId: val || null
                        }))}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    {selectedAnObject ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AnObjectField
                                value={selectedAnObject}
                                onChange={(obj) => {
                                    setSelectedAnObject(obj || null);
                                    setFilters(prev => ({
                                        ...prev,
                                        anObjectId: obj?.id || null
                                    }));
                                }}
                                disabled={loading}
                                label={selectedAnObject?.name || "Выбор объекта"}
                                showCreate={false}
                                showDetails={false}
                            />
                            <Button
                                variant="text"
                                onClick={() => {
                                    setSelectedAnObject(null);
                                    setFilters(prev => ({
                                        ...prev,
                                        anObjectId: null
                                    }));
                                }}
                            >
                                Очистить
                            </Button>
                        </Stack>
                    ) : (
                        <AnObjectField
                            value={selectedAnObject}
                            onChange={(obj) => {
                                setSelectedAnObject(obj || null);
                                setFilters(prev => ({
                                    ...prev,
                                    anObjectId: obj?.id || null
                                }));
                            }}
                            disabled={loading}
                            label="Выбор объекта"
                            showCreate={false}
                            showDetails={false}
                        />
                    )}
                </Grid>
            </Grid>

            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <LoadingButton loading={loading} onClick={() => fetchAsync()}>
                    Сформировать
                </LoadingButton>
                <LoadingButton 
                    loading={exportLoading} 
                    onClick={handleExcelExport}
                    variant="outlined"
                    color="success"
                >
                    Скачать Excel
                </LoadingButton>
            </Stack>

            <div style={{height: "10px"}}></div>
            
            <table style={{width: "100%"}}>
                <thead className="ant-table-thead">
                    <tr>
                        <th rowSpan={2} className="ant-table-cell">№</th>
                        <th rowSpan={2} style={{verticalAlign:"top"}} className="ant-table-cell">
                            Договора c ДСЭК  (Регионы)
                        </th>
                        <th rowSpan={2} style={{verticalAlign:"top"}} className="ant-table-cell">
                            Наименование объекта
                        </th>
                        {VISIBLE_DEPARTMENTS.map((dept) => (
                            <th key={dept.index} colSpan={6} style={{verticalAlign:"top"}} className="ant-table-cell">
                                {dept.name}
                            </th>
                        ))}
                        <th colSpan={6} style={{verticalAlign:"top"}} className="ant-table-cell">
                        Итого                       
                        </th>
                    </tr>
                    <tr>
                        {Array.from({ length: VISIBLE_GROUP_COUNT }).map((_, groupIndex) => (
                            <React.Fragment key={`group-${groupIndex}`}>
                                <th>план</th>
                                <th>сумма плана</th>
                                <th>факт</th>
                                <th>%</th>
                                <th>сумма</th>
                                <th>не соответствие</th>
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody className="ant-table-tbody">
                    {data && data.map((_, index) => {
                        const metricValues = [];
                        VISIBLE_DEPARTMENT_INDICES.forEach((i) => {
                            const plan = getPlanQuantity(_, i);
                            const planSum = getPlanSum(_, i);
                            const fact = getFactQuantity(_, i);
                            const percent = calcPercent(fact, plan);
                            const sumValue = getMetricValue(_, `sum${i}`);
                            const inconsistence = getInconsistenceQuantity(_, i);
                            metricValues.push(plan, planSum, fact, percent, sumValue, inconsistence);
                        });
                        const objectName = resolveObjectName(_);
                        const totalPlan = getPlanTotalQuantity(_);
                        const totalPlanSum = getPlanTotalSum(_);
                        const totalQuantity = getTotalQuantity(_);
                        const totalPercent = calcPercent(totalQuantity, totalPlan);
                        const totalSum = getTotalSumValue(_);
                        const totalInconsistence = getTotalInconsistence(_);
                        metricValues.push(totalPlan, totalPlanSum, totalQuantity, totalPercent, totalSum, totalInconsistence);
                        return <tr className="ant-table-row">
                            <td className="ant-table-cell">{index + 1}</td>
                            <td className="ant-table-cell">{_.name || ""}</td>
                            <td className="ant-table-cell">{objectName}</td>
                            {metricValues.map((value, valueIndex) => (
                                <td key={`metric-${index}-${valueIndex}`} className="ant-table-cell">{value}</td>
                            ))}
                        </tr>})
                    }
                    {data.length > 0 && <tr className="ant-table-row" style={{backgroundColor:"#3cb37188"}}>
                        <td></td>
                        <td>Всего:</td>
                        <td></td>
                        {[
                            ...VISIBLE_DEPARTMENT_INDICES.flatMap((i) => {
                                const plan = safeRoundTo2(sum(data, _ => getPlanQuantity(_, i)));
                                const planSum = safeRoundTo2(sum(data, _ => getPlanSum(_, i)));
                                const fact = safeRoundTo2(sum(data, _ => getFactQuantity(_, i)));
                                const percent = calcPercent(fact, plan);
                                const sumValue = safeRoundTo2(sum(data, _ => getMetricValue(_, `sum${i}`)));
                                const inconsistence = safeRoundTo2(sum(data, _ => getInconsistenceQuantity(_, i)));
                                return [plan, planSum, fact, percent, sumValue, inconsistence];
                            }),
                            safeRoundTo2(sum(data, _ => getPlanTotalQuantity(_))),
                            safeRoundTo2(sum(data, _ => getPlanTotalSum(_))),
                            totalQuantityAll,
                            calcPercent(
                                totalQuantityAll,
                                safeRoundTo2(sum(data, _ => getPlanTotalQuantity(_)))
                            ),
                            totalSumAll,
                            totalInconsistenceAll
                        ].map((value, valueIndex) => (
                            <td key={`total-metric-${valueIndex}`} className="ant-table-cell">{value}</td>
                        ))}
                    </tr>}
                </tbody>
            </table>
        </DashboardLayout>
    )

};

export default RequestTarificationsQuantityDepartments;
